import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Chip, LinearProgress,
  Slider, Switch, FormControlLabel, CircularProgress, Alert, IconButton,
  Collapse, Divider, Select, MenuItem, FormControl, InputLabel, OutlinedInput
} from '@mui/material';
import {
  CalendarToday, CheckCircle, PlayArrow, SkipNext, Refresh,
  ExpandMore, ExpandLess, TrendingUp, Star, Warning
} from '@mui/icons-material';
import FeatureGate from '../../components/subscription/FeatureGate';
import UpgradePrompt from '../../components/subscription/UpgradePrompt';
import api from '../../services/api';

const priorityColors = { high: '#f44336', medium: '#ff9800', low: '#4caf50' };
const statusIcons = {
  not_started: null,
  in_progress: <PlayArrow fontSize="small" sx={{ color: '#2196f3' }} />,
  completed: <CheckCircle fontSize="small" sx={{ color: '#4caf50' }} />,
  skipped: <SkipNext fontSize="small" sx={{ color: '#9e9e9e' }} />
};

function StudyPlanOnboarding({ onGenerate, loading }) {
  const [hours, setHours] = useState(5);
  const [weekends, setWeekends] = useState(false);
  const [focusTopics, setFocusTopics] = useState([]);
  const [availableTopics, setAvailableTopics] = useState([]);
  const [topicsLoading, setTopicsLoading] = useState(true);

  useEffect(() => {
    api.get('/api/study-plan/topics')
      .then(res => { if (res.data.success) setAvailableTopics(res.data.data.topics); })
      .catch(() => {})
      .finally(() => setTopicsLoading(false));
  }, []);

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 2 }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>Create Your Study Plan</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        We'll analyse your performance data and build a personalised weekly plan that targets your weak areas first.
      </Typography>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>How many hours can you study per week?</Typography>
          <Box sx={{ px: 2, pt: 1 }}>
            <Slider value={hours} onChange={(_, v) => setHours(v)} min={1} max={20} step={1}
              marks={[{ value: 1, label: '1h' }, { value: 5, label: '5h' }, { value: 10, label: '10h' }, { value: 20, label: '20h' }]}
              valueLabelDisplay="on" valueLabelFormat={v => `${v}h`} />
          </Box>
        </CardContent>
      </Card>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <FormControlLabel control={<Switch checked={weekends} onChange={e => setWeekends(e.target.checked)} />} label="Include weekends in my study plan" />
          <Typography variant="caption" color="text.secondary" display="block">{weekends ? '7 study days (Mon\u2013Sun)' : '5 study days (Mon\u2013Fri)'}</Typography>
        </CardContent>
      </Card>
      {!topicsLoading && availableTopics.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>Focus on specific topics? (optional)</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>Select up to 5 topics to prioritise. Leave empty for automatic priority based on your performance.</Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Focus Topics</InputLabel>
              <Select multiple value={focusTopics} onChange={e => { const val = e.target.value; if (val.length <= 5) setFocusTopics(val); }}
                input={<OutlinedInput label="Focus Topics" />}
                renderValue={selected => (<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>{selected.map(t => <Chip key={t} label={t} size="small" />)}</Box>)}>
                {availableTopics.map(t => (<MenuItem key={t} value={t}>{t}</MenuItem>))}
              </Select>
            </FormControl>
          </CardContent>
        </Card>
      )}
      <Button variant="contained" fullWidth size="large" onClick={() => onGenerate(hours, weekends, focusTopics)}
        disabled={loading} startIcon={loading ? <CircularProgress size={20} /> : <CalendarToday />} sx={{ py: 1.5, borderRadius: 2 }}>
        {loading ? 'Generating your plan...' : 'Generate My Study Plan'}
      </Button>
    </Box>
  );
}

function TaskCard({ task, onComplete, onSkip, onStart, loading }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <Card sx={{ mb: 1.5, borderLeft: `4px solid ${priorityColors[task.priority]}`, opacity: task.status === 'skipped' ? 0.6 : 1 }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
            {statusIcons[task.status]}
            <Typography variant="subtitle2" noWrap sx={{ fontWeight: 500 }}>{task.topic}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip label={`${task.questionCount}q \u00B7 ${task.estimatedMinutes}min`} size="small" variant="outlined" />
            <Chip label={task.priority} size="small" sx={{ bgcolor: priorityColors[task.priority], color: '#fff', fontSize: '0.7rem' }} />
            <IconButton size="small" onClick={() => setExpanded(!expanded)}>{expanded ? <ExpandLess /> : <ExpandMore />}</IconButton>
          </Box>
        </Box>
        <Collapse in={expanded}>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{task.description}</Typography>
          {task.rationale && (<Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>Why: {task.rationale}</Typography>)}
          {task.status !== 'completed' && task.status !== 'skipped' && (
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
              <Button variant="contained" size="small" startIcon={<PlayArrow />} onClick={() => onStart(task.taskId)} disabled={loading}>Start</Button>
              <Button variant="outlined" size="small" startIcon={<CheckCircle />} onClick={() => onComplete(task.taskId)} disabled={loading}>Mark Done</Button>
              <Button variant="text" size="small" color="inherit" startIcon={<SkipNext />} onClick={() => onSkip(task.taskId)} disabled={loading}>Skip</Button>
            </Box>
          )}
          {task.status === 'completed' && task.actualAccuracy != null && (
            <Chip label={`Accuracy: ${task.actualAccuracy}%`} size="small" color={task.actualAccuracy >= 75 ? 'success' : task.actualAccuracy >= 50 ? 'warning' : 'error'} sx={{ mt: 1 }} />
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
}

function WeeklyPlanView({ plan, onRegenerate, onTaskAction, loading }) {
  const dayGroups = {};
  plan.tasks.forEach(task => { if (!dayGroups[task.dayLabel]) dayGroups[task.dayLabel] = []; dayGroups[task.dayLabel].push(task); });
  const completionPercent = plan.completion.completionRate;

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Your Study Plan</Typography>
          <Typography variant="caption" color="text.secondary">
            {new Date(plan.weekStart).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })} \u2013{' '}
            {new Date(plan.weekEnd).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}
            {' \u00B7 '}Grade {plan.grade}{' \u00B7 '}{plan.parameters.availableHoursPerWeek}h/week
          </Typography>
        </Box>
        <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={onRegenerate} disabled={loading}>Regenerate</Button>
      </Box>
      <Card sx={{ mb: 2.5, bgcolor: 'background.default' }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="subtitle2">Weekly Progress</Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{plan.completion.completedTasks}/{plan.completion.totalTasks} tasks</Typography>
          </Box>
          <LinearProgress variant="determinate" value={completionPercent} sx={{ height: 8, borderRadius: 4 }} color={completionPercent >= 80 ? 'success' : completionPercent >= 40 ? 'warning' : 'primary'} />
        </CardContent>
      </Card>
      <Card sx={{ mb: 2.5 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="subtitle2" gutterBottom>Topic Priority Overview</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {plan.topicAssessments.map(a => (
              <Chip key={a.topic} label={`${a.topic}${a.accuracy != null ? ` (${a.accuracy}%)` : ''}`} size="small"
                icon={a.priority === 'high' ? <Warning /> : a.priority === 'low' ? <Star /> : <TrendingUp />}
                sx={{ borderColor: priorityColors[a.priority], color: priorityColors[a.priority], '& .MuiChip-icon': { color: priorityColors[a.priority] } }}
                variant="outlined" />
            ))}
          </Box>
        </CardContent>
      </Card>
      {Object.entries(dayGroups).map(([dayLabel, tasks]) => (
        <Box key={dayLabel} sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{dayLabel}</Typography>
          {tasks.map(task => (
            <TaskCard key={task.taskId} task={task}
              onComplete={taskId => onTaskAction('complete', taskId)}
              onSkip={taskId => onTaskAction('skip', taskId)}
              onStart={taskId => onTaskAction('start', taskId)} loading={loading} />
          ))}
        </Box>
      ))}
      {plan.status === 'completed' && (
        <Alert severity="success" sx={{ mt: 2 }}>Well done! You have completed this week's study plan. Generate a new one when you are ready for next week.</Alert>
      )}
    </Box>
  );
}

function StudyPlanContent() {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  const fetchPlan = useCallback(async () => {
    try { setLoading(true); const res = await api.get('/api/study-plan/current'); if (res.data.success) setPlan(res.data.data); }
    catch (err) { console.error('Failed to fetch plan:', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPlan(); }, [fetchPlan]);

  const handleGenerate = async (hours, weekends, focusTopics) => {
    try {
      setGenerating(true); setError(null);
      const res = await api.post('/api/study-plan/generate', { availableHoursPerWeek: hours, includeWeekends: weekends, focusTopics: focusTopics.length > 0 ? focusTopics : undefined });
      if (res.data.success) setPlan(res.data.data);
    } catch (err) { setError(err.response?.data?.error || 'Failed to generate study plan'); }
    finally { setGenerating(false); }
  };

  const handleTaskAction = async (action, taskId) => {
    try {
      setLoading(true);
      if (action === 'start') {
        const res = await api.get(`/api/study-plan/tasks/${taskId}/questions`);
        if (res.data.success) console.log('Task questions loaded:', res.data.data.questions.length);
      } else if (action === 'complete') {
        const res = await api.patch(`/api/study-plan/tasks/${taskId}/complete`, {});
        if (res.data.success) { setPlan(prev => { if (!prev) return prev; const u = { ...prev }; const t = u.tasks.find(t => t.taskId === taskId); if (t) t.status = 'completed'; u.completion = res.data.data.completion; u.status = res.data.data.planStatus; return u; }); return; }
      } else if (action === 'skip') {
        const res = await api.patch(`/api/study-plan/tasks/${taskId}/skip`);
        if (res.data.success) { setPlan(prev => { if (!prev) return prev; const u = { ...prev }; const t = u.tasks.find(t => t.taskId === taskId); if (t) t.status = 'skipped'; u.completion = res.data.data.completion; return u; }); return; }
      }
    } catch (err) { console.error('Task action failed:', err); }
    finally { setLoading(false); }
  };

  if (loading && !plan) return (<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>);

  return (
    <Box>
      {error && (<Alert severity="error" sx={{ mx: 2, mt: 2 }} onClose={() => setError(null)}>{error}</Alert>)}
      {plan ? (<WeeklyPlanView plan={plan} onRegenerate={() => { setPlan(null); setError(null); }} onTaskAction={handleTaskAction} loading={loading} />) : (<StudyPlanOnboarding onGenerate={handleGenerate} loading={generating} />)}
    </Box>
  );
}

export default function StudyPlanPage() {
  return (<FeatureGate feature="study_plans" fallback={<UpgradePrompt feature="study_plans" />}><StudyPlanContent /></FeatureGate>);
}
